import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { GuestAvatar } from "@/components/GuestAvatar";
import { Page } from "@/components/PageHeader";
import { money, tierColor, sentimentColor, dateStr, stageLabel, stageColor, consentColor, langLabel } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Crown, EnvelopeSimple, Phone, CalendarCheck, ChatCircleDots,
  Star, ClipboardText, Bed, IdentificationCard, Receipt, ArrowsLeftRight, ShieldCheck, Plus, Translate, Camera, Trash, PencilSimple,
} from "@phosphor-icons/react";

const LANGS = [
  ["en", "English"], ["es", "Spanish"], ["fr", "French"], ["de", "German"], ["it", "Italian"],
  ["pt", "Portuguese"], ["zh", "Chinese"], ["ja", "Japanese"], ["ar", "Arabic"],
];
const LIFECYCLE = [
  ["lead", "Lead"], ["reservation_confirmed", "Reservation confirmed"], ["checked_in", "Checked in"],
  ["checked_out", "Checked out"], ["no_show", "No show"], ["reservation_canceled", "Reservation canceled"],
];
const TIERS = ["Bronze", "Silver", "Gold", "Platinum"];

const eventMeta = {
  reservation: { icon: CalendarCheck, color: "text-blue-600 bg-blue-50" },
  email: { icon: EnvelopeSimple, color: "text-primary bg-primary/10" },
  stay: { icon: Bed, color: "text-violet-600 bg-violet-50" },
  chat: { icon: ChatCircleDots, color: "text-cyan-600 bg-cyan-50" },
  review: { icon: Star, color: "text-amber-600 bg-amber-50" },
  survey: { icon: ClipboardText, color: "text-emerald-600 bg-emerald-50" },
};

const commIcon = {
  email: EnvelopeSimple, sms: ChatCircleDots, chat: ChatCircleDots, call: Phone,
};

const txnColor = {
  completed: "text-success bg-success/10 border-success/20",
  pending: "text-amber-600 bg-amber-50 border-amber-200",
  canceled: "text-destructive bg-destructive/10 border-destructive/20",
  refunded: "text-slate-600 bg-slate-100 border-slate-200",
};

export default function GuestProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["guest", id], queryFn: async () => (await api.get(`/guests/${id}`)).data });

  if (isLoading || !data) return <Page><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></Page>;
  const g = data.guest;
  const consent = g.consent || {};

  const invalidate = () => qc.invalidateQueries({ queryKey: ["guest", id] });

  return (
    <Page>
      <button data-testid="back-to-guests" onClick={() => navigate("/app/guests")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5 transition-colors">
        <ArrowLeft size={16} /> Back to guests
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* profile card */}
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-card p-6 fade-up">
            <div className="flex flex-col items-center text-center">
              <AvatarEditor guest={g} onChange={invalidate} />
              <div className="mt-3 flex items-center gap-1.5">
                <h2 className="font-heading text-xl font-semibold">{g.name}</h2>
                {g.vip && <Crown size={16} weight="fill" className="text-primary" />}
                <EditGuestDialog guest={g} onDone={invalidate} />
              </div>
              {g.lifecycle_status && (
                <span data-testid="guest-lifecycle-badge" className={`mt-2 text-[11px] px-2.5 py-0.5 rounded-full border ${stageColor[g.lifecycle_status] || "bg-secondary border-border"}`}>
                  {stageLabel[g.lifecycle_status] || g.lifecycle_status}
                </span>
              )}
              <span className={`mt-2 text-[11px] px-2.5 py-0.5 rounded-full border ${tierColor[g.loyalty_tier]}`}>{g.loyalty_tier} Member</span>
            </div>
            <div className="mt-5 space-y-2.5 text-sm">
              <div className="flex items-center gap-2.5 text-muted-foreground"><EnvelopeSimple size={16} /><span className="text-foreground truncate">{g.email}</span></div>
              <div className="flex items-center gap-2.5 text-muted-foreground"><Phone size={16} /><span className="text-foreground">{g.phone}</span></div>
              {g.language && <div className="flex items-center gap-2.5 text-muted-foreground" data-testid="guest-language"><Translate size={16} /><span className="text-foreground">{langLabel(g.language)}</span><span className="text-[10px] text-muted-foreground">· from PMS</span></div>}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-5">
              <div className="rounded-md border border-border p-3 text-center">
                <div className="font-heading text-lg font-semibold">{money(g.lifetime_value)}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Lifetime value</div>
              </div>
              <div className="rounded-md border border-border p-3 text-center">
                <div className="font-heading text-lg font-semibold">{g.total_stays}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Total stays</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground mb-2">Tags</div>
              <div className="flex flex-wrap gap-1.5">
                {(g.tags || []).map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary border border-border">{t}</span>)}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-5 fade-up" style={{ animationDelay: "80ms" }}>
            <div className="flex items-center gap-2 mb-3">
              <IdentificationCard size={18} className="text-primary" />
              <h3 className="font-heading font-semibold text-sm">Identity resolution</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">This profile was matched across {(g.identity_sources || []).length} data sources.</p>
            <div className="flex flex-wrap gap-1.5">
              {(g.identity_sources || []).map((s) => <span key={s} className="text-[11px] px-2 py-0.5 rounded bg-success/10 text-success border border-success/20">{s}</span>)}
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-xs">
              <span className={`px-2 py-0.5 rounded border ${sentimentColor[g.sentiment]}`}>Sentiment: {g.sentiment}</span>
            </div>
          </div>
        </div>

        {/* tabbed detail */}
        <div className="lg:col-span-2 rounded-lg border border-border bg-card p-6 fade-up" style={{ animationDelay: "40ms" }}>
          <Tabs defaultValue="timeline">
            <TabsList data-testid="profile-tabs" className="mb-5">
              <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
              <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
              <TabsTrigger value="communications" data-testid="tab-communications">Communications</TabsTrigger>
              <TabsTrigger value="consent" data-testid="tab-consent">Consent</TabsTrigger>
            </TabsList>

            {/* TIMELINE */}
            <TabsContent value="timeline">
              <div className="relative pl-2">
                <div className="absolute left-[19px] top-1 bottom-1 w-px bg-border" />
                <div className="space-y-5">
                  {data.timeline.map((e, i) => {
                    const m = eventMeta[e.type] || eventMeta.email;
                    return (
                      <div key={e.id} className="relative flex gap-4 fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                        <div className={`z-10 h-9 w-9 rounded-full flex items-center justify-center border-2 border-background ${m.color}`}>
                          <m.icon size={16} weight="fill" />
                        </div>
                        <div className="flex-1 pb-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-sm">{e.title}</span>
                            <span className="text-[11px] text-muted-foreground shrink-0">{dateStr(e.timestamp)}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{e.description}</p>
                          <span className="inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded bg-secondary border border-border text-muted-foreground">{e.channel}</span>
                        </div>
                      </div>
                    );
                  })}
                  {data.timeline.length === 0 && <p className="text-sm text-muted-foreground">No timeline events yet.</p>}
                </div>
              </div>
            </TabsContent>

            {/* TRANSACTIONS */}
            <TabsContent value="transactions">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading font-semibold text-sm">Transaction history</h3>
                  <p className="text-xs text-muted-foreground">Bookings, dining, spa & refunds.</p>
                </div>
                <AddTransactionDialog guestId={id} onDone={invalidate} />
              </div>
              <div className="space-y-2">
                {data.transactions.map((t) => (
                  <div key={t.id} data-testid={`txn-row-${t.id}`} className="flex items-center justify-between rounded-md border border-border p-3">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Receipt size={16} /></div>
                      <div>
                        <div className="text-sm font-medium capitalize">{t.type} <span className="font-normal text-muted-foreground">· {t.description}</span></div>
                        <div className="text-[11px] text-muted-foreground">{dateStr(t.date)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border capitalize ${txnColor[t.status] || "bg-secondary border-border"}`}>{t.status}</span>
                      <span className={`text-sm font-semibold tabular-nums ${t.amount < 0 ? "text-destructive" : ""}`}>{t.amount < 0 ? "-" : ""}{money(Math.abs(t.amount))}</span>
                    </div>
                  </div>
                ))}
                {data.transactions.length === 0 && <p className="text-sm text-muted-foreground">No transactions recorded.</p>}
              </div>
            </TabsContent>

            {/* COMMUNICATIONS */}
            <TabsContent value="communications">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading font-semibold text-sm">Communications</h3>
                  <p className="text-xs text-muted-foreground">Emails, SMS, chats & calls across channels.</p>
                </div>
                <LogCommunicationDialog guestId={id} onDone={invalidate} />
              </div>
              <div className="space-y-2">
                {data.communications.map((c) => {
                  const Icon = commIcon[c.channel] || EnvelopeSimple;
                  return (
                    <div key={c.id} data-testid={`comm-row-${c.id}`} className="flex items-start gap-3 rounded-md border border-border p-3">
                      <div className="h-9 w-9 shrink-0 rounded-full bg-secondary flex items-center justify-center"><Icon size={16} className="text-muted-foreground" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium truncate">{c.subject || c.channel}</span>
                          <span className="text-[11px] text-muted-foreground shrink-0">{dateStr(c.timestamp)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{c.snippet}</p>
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary border border-border capitalize flex items-center gap-1"><ArrowsLeftRight size={10} />{c.direction}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary border border-border capitalize">{c.channel}</span>
                          {c.status && <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary border border-border capitalize">{c.status}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {data.communications.length === 0 && <p className="text-sm text-muted-foreground">No communications logged.</p>}
              </div>
            </TabsContent>

            {/* CONSENT */}
            <TabsContent value="consent">
              <div className="mb-4">
                <h3 className="font-heading font-semibold text-sm flex items-center gap-1.5"><ShieldCheck size={16} className="text-primary" /> Marketing consent</h3>
                <p className="text-xs text-muted-foreground">Per-channel opt-in status. Changes are logged for compliance.</p>
              </div>
              <div className="space-y-3">
                {["email", "sms", "voice"].map((ch) => (
                  <ConsentRow key={ch} guestId={id} channel={ch} record={consent[ch] || { status: "unknown" }} onDone={invalidate} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Page>
  );
}

function ConsentRow({ guestId, channel, record, onDone }) {
  const status = record.status || "unknown";
  const mut = useMutation({
    mutationFn: async (next) => (await api.put(`/guests/${guestId}/consent`, { channel, status: next })).data,
    onSuccess: () => { onDone(); toast.success(`${channel.toUpperCase()} consent updated`); },
  });
  return (
    <div data-testid={`consent-row-${channel}`} className="flex items-center justify-between rounded-md border border-border p-3">
      <div>
        <div className="text-sm font-medium capitalize">{channel}</div>
        <div className="text-[11px] text-muted-foreground">
          {record.source ? `via ${record.source}` : "no record"}{record.timestamp ? ` · ${dateStr(record.timestamp)}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-2.5">
        <span className={`text-[11px] px-2 py-0.5 rounded-full border capitalize ${consentColor[status] || consentColor.unknown}`}>{status.replace("_", " ")}</span>
        <Select value={status} onValueChange={(v) => mut.mutate(v)}>
          <SelectTrigger className="w-[130px] h-8 text-xs" data-testid={`consent-select-${channel}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="opted_in">Opted in</SelectItem>
            <SelectItem value="opted_out">Opted out</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function LogCommunicationDialog({ guestId, onDone }) {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("email");
  const [direction, setDirection] = useState("outbound");
  const [subject, setSubject] = useState("");
  const [snippet, setSnippet] = useState("");
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/guests/${guestId}/communications`, { channel, direction, subject, snippet })).data,
    onSuccess: () => { onDone(); toast.success("Communication logged"); setOpen(false); setSubject(""); setSnippet(""); },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="log-comm-btn"><Plus size={14} className="mr-1" /> Log note</Button>
      </DialogTrigger>
      <DialogContent data-testid="log-comm-dialog">
        <DialogHeader><DialogTitle>Log communication</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="comm-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="chat">Chat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Direction</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger data-testid="comm-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="outbound">Outbound</SelectItem>
                  <SelectItem value="inbound">Inbound</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Follow-up call" data-testid="comm-subject" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Notes</Label>
            <Textarea value={snippet} onChange={(e) => setSnippet(e.target.value)} placeholder="What was discussed…" data-testid="comm-snippet" />
          </div>
        </div>
        <DialogFooter>
          <Button disabled={!snippet.trim() || mut.isPending} onClick={() => mut.mutate()} data-testid="comm-save-btn">
            {mut.isPending ? "Saving…" : "Log communication"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddTransactionDialog({ guestId, onDone }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("reservation");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [status, setStatus] = useState("completed");
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/guests/${guestId}/transactions`, { type, description, amount: parseFloat(amount), status })).data,
    onSuccess: () => { onDone(); toast.success("Transaction added"); setOpen(false); setDescription(""); setAmount(""); },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="add-txn-btn"><Plus size={14} className="mr-1" /> Add</Button>
      </DialogTrigger>
      <DialogContent data-testid="add-txn-dialog">
        <DialogHeader><DialogTitle>Add transaction</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="txn-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="reservation">Reservation</SelectItem>
                  <SelectItem value="dining">Dining</SelectItem>
                  <SelectItem value="spa">Spa</SelectItem>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="refund">Refund</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Amount ($)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" data-testid="txn-amount" />
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Room · Deluxe King" data-testid="txn-description" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="txn-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="canceled">Canceled</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button disabled={!amount || mut.isPending} onClick={() => mut.mutate()} data-testid="txn-save-btn">
            {mut.isPending ? "Saving…" : "Add transaction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditGuestDialog({ guest, onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(guest.name || "");
  const [email, setEmail] = useState(guest.email || "");
  const [phone, setPhone] = useState(guest.phone || "");
  const [language, setLanguage] = useState(guest.language || "en");
  const [tier, setTier] = useState(guest.loyalty_tier || "Bronze");
  const [lifecycle, setLifecycle] = useState(guest.lifecycle_status || "lead");
  const [vip, setVip] = useState(!!guest.vip);
  const reset = () => {
    setName(guest.name || ""); setEmail(guest.email || ""); setPhone(guest.phone || "");
    setLanguage(guest.language || "en"); setTier(guest.loyalty_tier || "Bronze");
    setLifecycle(guest.lifecycle_status || "lead"); setVip(!!guest.vip);
  };
  const mut = useMutation({
    mutationFn: async () => (await api.put(`/guests/${guest.id}`, {
      name: name.trim(), email: email.trim(), phone: phone.trim(), language,
      loyalty_tier: tier, lifecycle_status: lifecycle, vip,
    })).data,
    onSuccess: () => { onDone(); toast.success("Profile updated"); setOpen(false); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed"),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid="edit-guest-btn" title="Edit profile" className="ml-1 h-6 w-6 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary flex items-center justify-center transition-colors">
          <PencilSimple size={14} />
        </button>
      </DialogTrigger>
      <DialogContent data-testid="edit-guest-dialog">
        <DialogHeader><DialogTitle>Edit guest profile</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs mb-1 block">Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-guest-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="edit-guest-email" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="edit-guest-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="edit-guest-language"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGS.map(([c, l]) => <SelectItem key={c} value={c}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Loyalty tier</Label>
              <Select value={tier} onValueChange={setTier}>
                <SelectTrigger data-testid="edit-guest-tier"><SelectValue /></SelectTrigger>
                <SelectContent>{TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Lifecycle stage</Label>
            <Select value={lifecycle} onValueChange={setLifecycle}>
              <SelectTrigger data-testid="edit-guest-lifecycle"><SelectValue /></SelectTrigger>
              <SelectContent>{LIFECYCLE.map(([c, l]) => <SelectItem key={c} value={c}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={vip} onChange={(e) => setVip(e.target.checked)} data-testid="edit-guest-vip" className="h-4 w-4 rounded border-border" />
            Mark as VIP
          </label>
        </div>
        <DialogFooter>
          <Button disabled={!name.trim() || mut.isPending} onClick={() => mut.mutate()} data-testid="edit-guest-save-btn">
            {mut.isPending ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AvatarEditor({ guest, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5 MB)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/guests/${guest.id}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile picture updated");
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/guests/${guest.id}/avatar`);
      toast.success("Profile picture removed");
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="relative group" data-testid="avatar-editor">
      <GuestAvatar src={guest.avatar} name={guest.name} className="h-20 w-20 rounded-full border-2 border-border" textClassName="text-xl font-semibold" />
      <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="avatar-file-input" />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        title="Change photo"
        data-testid="avatar-upload-btn"
        className="absolute -bottom-0.5 -right-0.5 h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md border-2 border-card hover:scale-105 transition-transform disabled:opacity-60"
      >
        <Camera size={14} weight="fill" />
      </button>
      {guest.avatar && (
        <button
          type="button"
          onClick={remove}
          disabled={busy}
          title="Remove photo"
          data-testid="avatar-delete-btn"
          className="absolute -top-0.5 -right-0.5 h-6 w-6 rounded-full bg-card text-destructive flex items-center justify-center shadow-md border border-border opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-60"
        >
          <Trash size={12} weight="fill" />
        </button>
      )}
    </div>
  );
}
